import React, { useEffect, useState } from "react";
import axios from "axios";
import "./festival_sales_subscription.scss";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";

interface SubscriptionPlan {
  _id: string;
  planName: string;
  description: string;
  mrp: number;
  price: number;
  discount: number;
  durationInMonths: number;
  isWithHerbs?: boolean;
}

interface PlanUpdate {
  newMrp?: number;
  newPrice?: number;
  newDiscount?: number;
  startTime?: string;
  endTime?: string;
}

const FestivalSalesSubscription: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
  const [planUpdates, setPlanUpdates] = useState<Record<string, PlanUpdate>>(
    {}
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const navigate = useNavigate();
  const [showOverlay, setShowOverlay] = useState<boolean>(false);

  useEffect(() => {
    axios
      .get("https://api.fitoja.in/api/get-all-subscription-plans")
      .then((res) => setPlans(res.data.data || []))
      .catch(() => setMessage("Failed to fetch subscription plans."));
  }, []);

  const handlePlanSelection = (id: string) => {
    setSelectedPlanIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const updateField = (
    planId: string,
    field: keyof PlanUpdate,
    value: string
  ) => {
    setPlanUpdates((prev) => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        [field]: value
          ? field.includes("Time")
            ? value
            : Number(value)
          : undefined,
      },
    }));
  };

  const convertISTtoUTC = (istTimeString: string) => {
    const localIST = new Date(
      new Date(istTimeString).toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
      })
    );
    return new Date(localIST.toISOString());
  };

  const handleSubmit = async () => {
    setMessage("");
    var startSaleTiming;
    var endSaleTiming;

    const updatesPayload = selectedPlanIds.map((planId) => {
      const update = planUpdates[planId];
      if (
        !update ||
        update.newMrp === undefined ||
        update.newPrice === undefined ||
        !update.startTime ||
        !update.endTime
      ) {
        return null; // skip invalid
      }

      const discount =
        update.newDiscount !== undefined
          ? update.newDiscount
          : Math.max(
              0,
              Math.round(
                ((update.newMrp - update.newPrice) / update.newMrp) * 100
              )
            );
      startSaleTiming = update.startTime;
      endSaleTiming = update.endTime;
      return {
        subscriptionId: planId,
        newMrp: update.newMrp,
        newPrice: update.newPrice,
        newDiscount: discount,
        startTime: convertISTtoUTC(update.startTime),
        endTime: convertISTtoUTC(update.endTime),
      };
    });

    const validUpdates = updatesPayload.filter(Boolean);

    if (validUpdates.length === 0) {
      setMessage("Please fill all required fields for selected plans.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        "https://api.fitoja.in/api/schedule-subscription-update",
        {
          updates: validUpdates,
        }
      );

      if (response.data?.results?.every((r: any) => r.success)) {
        setMessage("All price updates scheduled successfully.");
        try {
          const response = await axios.put(
            "https://api.fitoja.in/api/update-sale-timing-subscription",
            {
              saleStartsAt: convertISTtoUTC(startSaleTiming),
              saleEndsAt: convertISTtoUTC(endSaleTiming),
            }
          );

          if (response.data?.results?.every((r: any) => r.success)) {
            setMessage("Successfully  updated start and end sales timing");
          } else {
            setMessage("Failed to update Start and Update end timing");
          }
        } catch (error) {
          setMessage("Error updating the start and end sales timing.");
        } finally {
          setLoading(false);
        }
      } else {
        setMessage("Some updates failed. Please review input.");
      }
    } catch (error) {
      setMessage("Error scheduling price updates.");
    } finally {
      //   setLoading(false);
    }
  };

  return (
    <div className="festival-subscription-container">
      <header className="dashboard-header" style={{marginLeft: "-20px"}}>
        <div className="logo" onClick={() => navigate("/dashboard")}>
          <img src={logo} alt="Logo" />
        </div>
        <div
          className="profile-section"
          onClick={() => setShowOverlay(!showOverlay)}
        >
          <img className="profile-pic" src={maleProfilePic} alt="Profile" />
        </div>
      </header>
      <br></br>
      <br></br>
      <br></br>
      <h2>Schedule Festival Sale Offer</h2>

      <div className="form-group">
        <label>Select Subscription Plans:</label>
        <div className="checkbox-list">
          {plans.map((plan) => (
            <label key={plan._id}>
              <input
                type="checkbox"
                checked={selectedPlanIds.includes(plan._id)}
                onChange={() => handlePlanSelection(plan._id)}
              />
              {plan.planName} (₹{plan.price})
            </label>
          ))}
        </div>
      </div>

      {selectedPlanIds.map((planId) => {
        const update = planUpdates[planId] || {};
        const plan = plans.find((p) => p._id === planId);

        return (
          <div key={planId} className="plan-edit-section">
            <h4>{plan?.planName}</h4>

            <div className="form-row">
              <div className="form-group">
                <label>New MRP:</label>
                <input
                  type="number"
                  min="0"
                  value={update.newMrp ?? ""}
                  onChange={(e) =>
                    updateField(planId, "newMrp", e.target.value)
                  }
                />
              </div>

              <div className="form-group">
                <label>New Price:</label>
                <input
                  type="number"
                  min="0"
                  value={update.newPrice ?? ""}
                  onChange={(e) =>
                    updateField(planId, "newPrice", e.target.value)
                  }
                />
              </div>

              <div className="form-group">
                <label>New Discount (%):</label>
                <input
                  type="number"
                  min="0"
                  value={update.newDiscount ?? ""}
                  onChange={(e) =>
                    updateField(planId, "newDiscount", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Time:</label>
                <input
                  type="datetime-local"
                  value={update.startTime ?? ""}
                  onChange={(e) =>
                    updateField(planId, "startTime", e.target.value)
                  }
                />
              </div>

              <div className="form-group">
                <label>End Time:</label>
                <input
                  type="datetime-local"
                  value={update.endTime ?? ""}
                  onChange={(e) =>
                    updateField(planId, "endTime", e.target.value)
                  }
                />
              </div>
            </div>

            <hr />
          </div>
        );
      })}

      <button onClick={handleSubmit} disabled={loading}>
        {loading ? "Scheduling..." : "Schedule Festival Offers"}
      </button>

      {message && <p className="status-message">{message}</p>}
    </div>
  );
};

export default FestivalSalesSubscription;
